import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Agent, SendMessageOptions } from '../types';

type MentionTrigger = '@' | '/';

interface MentionState {
  start: number;
  end: number;
  trigger: MentionTrigger;
  query: string;
}

interface InputBoxProps {
  onSendMessage: (content: string, options?: SendMessageOptions) => void;
  availableAgents: Agent[];
  disabled?: boolean;
  placeholder?: string;
  isStreaming?: boolean;
  isLockedChat?: boolean; // When true, disable @ / mention functionality (locked to specific agent)
}

const InputBox: React.FC<InputBoxProps> = ({
  onSendMessage,
  availableAgents,
  disabled = false,
  placeholder = 'Type a message...',
  isStreaming = false,
  isLockedChat = false
}) => {
  const [message, setMessage] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [forcedAgent, setForcedAgent] = useState<Agent | null>(null);
  const [mentionState, setMentionState] = useState<MentionState | null>(null);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const agentButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const agentOptions = useMemo(() => availableAgents ?? [], [availableAgents]);

  const filteredAgents = useMemo(() => {
    // Filter out hidden agents (like Aldo routing agent) from the agent list
    const visibleAgents = agentOptions.filter(agent => 
      !agent.hidden
    );
    
    if (!mentionState) {
      return visibleAgents;
    }
    const query = mentionState.query.toLowerCase();
    
    // Filter and sort: prioritize name/id matches over description matches
    const matches = visibleAgents
      .filter(agent => 
        agent.name.toLowerCase().includes(query) ||
        agent.id.toLowerCase().includes(query) ||
        (agent.description || '').toLowerCase().includes(query)
      )
      .sort((a, b) => {
        const aNameMatch = a.name.toLowerCase().includes(query);
        const aIdMatch = a.id.toLowerCase().includes(query);
        const bNameMatch = b.name.toLowerCase().includes(query);
        const bIdMatch = b.id.toLowerCase().includes(query);
        
        // Prioritize name/id matches over description matches
        const aScore = (aNameMatch || aIdMatch) ? 1 : 0;
        const bScore = (bNameMatch || bIdMatch) ? 1 : 0;
        
        if (aScore !== bScore) {
          return bScore - aScore; // Higher score first
        }
        
        // If both match name or both match description, sort alphabetically
        return a.name.localeCompare(b.name);
      });
    
    return matches;
  }, [agentOptions, mentionState]);

  useEffect(() => {
    setHighlightIndex(0);
  }, [mentionState?.query]);

  // Scroll highlighted item into view with next item partially visible
  useEffect(() => {
    if (mentionState && agentButtonRefs.current[highlightIndex]) {
      const element = agentButtonRefs.current[highlightIndex];
      const container = element?.parentElement;
      
      if (element && container) {
        const elementRect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const itemHeight = elementRect.height;
        
        // Calculate scroll position to show ~40% of next item
        const desiredTop = element.offsetTop - (containerRect.height - itemHeight * 1.4);
        
        container.scrollTo({
          top: Math.max(0, desiredTop),
          behavior: 'smooth'
        });
      }
    }
  }, [highlightIndex, mentionState]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && !disabled && !isStreaming) {
      onSendMessage(message.trim(), forcedAgent ? { forcedAgentId: forcedAgent.id } : undefined);
      setMessage('');
      setForcedAgent(null);
      setMentionState(null);
    }
  };

  const detectMention = (value: string, cursor: number): MentionState | null => {
    for (let i = cursor - 1; i >= 0; i--) {
      const char = value[i];
      if (char === '@' || char === '/') {
        const prevChar = i === 0 ? ' ' : value[i - 1];
        if (i === 0 || /\s/.test(prevChar)) {
          const query = value.slice(i + 1, cursor);
          if (!query.includes(' ') && !query.includes('\n')) {
            return { start: i, end: cursor, trigger: char as MentionTrigger, query };
          }
        }
        break;
      }
    }
    return null;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (mentionState && filteredAgents.length > 0) {
      // Tab and ArrowDown both move down the list
      if (e.key === 'Tab' || e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIndex((prev) => (prev + 1) % filteredAgents.length);
        return;
      }
      // ArrowUp moves up the list
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIndex((prev) => (prev - 1 + filteredAgents.length) % filteredAgents.length);
        return;
      }
      // Only Enter/Return selects the highlighted agent
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        const selected = filteredAgents[highlightIndex] || filteredAgents[0];
        if (selected) {
          applyForcedAgent(selected);
        }
        return;
      }
      // Escape closes the mention menu
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionState(null);
        return;
      }
    }

    // Normal Enter submits the form (when not in mention mode)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleSendClick = () => {
    if (message.trim() && !disabled && !isStreaming) {
      onSendMessage(message.trim(), forcedAgent ? { forcedAgentId: forcedAgent.id } : undefined);
      setMessage('');
      setForcedAgent(null);
      setMentionState(null);
    }
  };

  useEffect(() => {
    if (textareaRef.current) {
      const maxHeight = 96;
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      if (scrollHeight > maxHeight) {
        textareaRef.current.style.height = `${maxHeight}px`;
        textareaRef.current.style.overflowY = 'auto';
      } else {
        textareaRef.current.style.height = `${scrollHeight}px`;
        textareaRef.current.style.overflowY = 'hidden';
      }
    }
  }, [message]);

  const applyForcedAgent = (agent: Agent) => {
    if (mentionState) {
      const before = message.slice(0, mentionState.start);
      const after = message.slice(mentionState.end);
      setMessage(`${before}${after}`.replace(/\s{2,}/g, ' ').trimStart());
    }
    setForcedAgent(agent);
    setMentionState(null);
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setMessage(value);
    // Disable mention detection in locked chat mode
    if (!isLockedChat) {
      const cursor = e.target.selectionStart || value.length;
      const mention = detectMention(value, cursor);
      setMentionState(mention);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto" style={{ marginBottom: 0, paddingBottom: 0 }}>
      <form onSubmit={handleSubmit} className="flex items-end gap-3" style={{ marginBottom: 0, paddingBottom: 0 }}>
        <div className="flex-1 relative">
          {forcedAgent && (
            <div className="absolute -top-8 left-0 flex items-center gap-2 text-xs text-teal-700 dark:text-teal-300">
              <span className="font-semibold">Routing to {forcedAgent.name}</span>
              <button
                type="button"
                onClick={() => setForcedAgent(null)}
                className="px-1 rounded-full bg-teal-50 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300 hover:bg-teal-100 dark:hover:bg-teal-800/50"
              >
                ×
              </button>
            </div>
          )}
          <div className={`flex items-center border rounded-3xl px-4 py-3 bg-white dark:bg-gray-900 transition-all duration-200 ${
            isFocused 
              ? 'border-teal-500 ring-2 ring-teal-500/20' 
              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
          }`} style={{
            boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.05)',
            marginBottom: 0,
            paddingBottom: '12px'
          }}>
            <textarea
              ref={textareaRef}
              value={message}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder={placeholder}
              disabled={disabled || isStreaming}
              className="flex-1 resize-none border-none outline-none bg-transparent text-sm leading-relaxed text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
              style={{ maxHeight: '96px', overflowY: 'auto' }}
              rows={1}
              maxLength={4000}
            />
            <div className="flex items-center gap-2 ml-2">
              <button
                type="submit"
                onClick={handleSendClick}
                disabled={!message.trim() || disabled || isStreaming}
                className={`px-4 py-2 rounded-xl font-medium text-sm transition-all duration-200 ${
                  message.trim() && !disabled && !isStreaming
                    ? 'bg-teal-500 text-white hover:bg-teal-600 shadow-md hover:shadow-lg'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                }`}
              >
                {isStreaming ? (
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Sending...</span>
                  </div>
                ) : (
                  'Send'
                )}
              </button>
            </div>
          </div>
          {/* Mention dropdown - hidden in locked chat mode */}
          {!isLockedChat && mentionState && filteredAgents.length > 0 && (
            <div className="absolute bottom-full left-0 mb-2 w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-lg z-10 max-h-[240px] overflow-y-auto">
              {filteredAgents.map((agent, index) => (
                <button
                  type="button"
                  key={agent.id}
                  ref={(el) => { agentButtonRefs.current[index] = el; }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    applyForcedAgent(agent);
                  }}
                  className={`w-full text-left px-4 py-2 rounded-2xl transition-colors ${
                    index === highlightIndex ? 'bg-teal-50 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="text-sm font-medium">{agent.name}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{agent.description}</div>
                </button>
              ))}
              {filteredAgents.length === 0 && (
                <div className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400">No agents found</div>
              )}
            </div>
          )}
          {/* Helper text - hidden in locked chat mode */}
          {!isLockedChat && (
            <div className="mt-2 text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                Optional: type @ or / to list and select from your available agents
              </p>
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default InputBox;